import { recalcNota } from './inventarioCalculos';
import { ESTADO_DISPONIBLE } from './inventarioEstados';

function normalizeCompositionValue(v) {
  if (v == null || v === '') return '';
  return String(v).trim().replace(',', '.').toUpperCase();
}

export function compositionKey(linea) {
  return ['grado', 'espesor', 'ancho', 'largo']
    .map((field) => normalizeCompositionValue(linea?.[field]))
    .join('|');
}

const COMPOSICION_COPY_FIELDS = ['paquetes', 'pcs_paq', 'altura_rw', 'castigo_rw'];

function cloneLineaComposicion(linea) {
  const out = {};
  COMPOSICION_COPY_FIELDS.forEach((field) => {
    out[field] = linea?.[field] ?? '';
  });
  return out;
}

function limpiarLineaPlantilla(linea) {
  return {
    ...linea,
    id: undefined,
    orden: undefined,
    estado_mercaderia: ESTADO_DISPONIBLE,
    m3_proveedor: null,
    unidad_proveedor: '',
    grado_proveedor: '',
    valor_brl_proveedor: null,
    producto_xml: '',
    _total_m3_manual: false,
    _total_brl_m3_manual: false,
  };
}

function compositionKeyUsable(key) {
  return Boolean(key.replace(/\|/g, ''));
}

function buildXmlLineasPools(xmlLineas) {
  const pools = new Map();
  for (const ln of xmlLineas || []) {
    const key = compositionKey(ln);
    if (!compositionKeyUsable(key)) continue;
    if (!pools.has(key)) pools.set(key, []);
    pools.get(key).push(ln);
  }
  return pools;
}

function takeXmlMatch(pools, key) {
  if (!compositionKeyUsable(key)) return null;
  const queue = pools.get(key);
  if (!queue?.length) return null;
  return queue.shift();
}

function mergeXmlConPlantilla(prevLn, xmlLn) {
  const base = limpiarLineaPlantilla(prevLn);
  return {
    ...base,
    ...xmlLn,
    grado: base.grado,
    espesor: base.espesor,
    ancho: base.ancho,
    largo: base.largo,
    ...cloneLineaComposicion(prevLn),
    _total_m3_manual: false,
    _total_brl_m3_manual: false,
  };
}

function lineasRestantesEnPools(pools) {
  const out = [];
  for (const queue of pools.values()) out.push(...queue);
  return out;
}

export function buscarNfComposicion({ guardadas, idProveedor, idProducto, productoIds = [] }) {
  const prov = Number(idProveedor);
  const prod = Number(idProducto);
  if (!Number.isFinite(prov) || !Number.isFinite(prod)) return null;
  const productosCompatibles = new Set(
    (productoIds.length ? productoIds : [prod]).map(Number).filter(Number.isFinite)
  );
  return (guardadas || []).find(
    (nf) =>
      Number(nf.id_proveedor) === prov &&
      productosCompatibles.has(Number(nf.id_producto)) &&
      Array.isArray(nf.lineas) &&
      nf.lineas.length > 0
  );
}

/**
 * @param {'xml'|'manual'} modo — manual clona líneas; xml replica estructura de nfAnterior y fusiona ítems XML por grado/medida.
 */
export function aplicarComposicionAnterior(nota, nfAnterior, { modo = 'xml' } = {}) {
  if (!nota || !nfAnterior?.lineas?.length) {
    return { nota, copiadas: 0 };
  }

  const prevByKey = new Map();
  nfAnterior.lineas.forEach((ln) => {
    const key = compositionKey(ln);
    if (compositionKeyUsable(key)) prevByKey.set(key, ln);
  });

  if (modo === 'manual') {
    const lineas = nfAnterior.lineas.map((ln) => limpiarLineaPlantilla(ln));
    return {
      nota: recalcNota({ ...nota, lineas }),
      copiadas: lineas.length,
    };
  }

  const xmlPools = buildXmlLineasPools(nota.lineas || []);
  let conDatosXml = 0;
  const lineas = nfAnterior.lineas.map((prevLn) => {
    const key = compositionKey(prevLn);
    const xmlLn = takeXmlMatch(xmlPools, key);
    if (!xmlLn) return limpiarLineaPlantilla(prevLn);
    conDatosXml += 1;
    return mergeXmlConPlantilla(prevLn, xmlLn);
  });

  for (const xmlLn of lineasRestantesEnPools(xmlPools)) {
    const prev = prevByKey.get(compositionKey(xmlLn));
    lineas.push(
      prev
        ? mergeXmlConPlantilla(prev, xmlLn)
        : {
            ...xmlLn,
            _total_m3_manual: false,
            _total_brl_m3_manual: false,
          }
    );
    conDatosXml += 1;
  }

  return {
    nota: recalcNota({ ...nota, lineas }),
    copiadas: lineas.length,
    conDatosXml,
  };
}
